// ============================================================
// ByOurConsent.com -- Elected Official Accountability
// /premium/official-accountability
// Premium member tool -- Official Accountability Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const POSITION_TYPES = [
    { id: 'us_president',     label: 'President of the United States' },
    { id: 'us_senator',       label: 'U.S. Senator' },
    { id: 'us_representative',label: 'U.S. Representative / Congressman' },
    { id: 'governor',         label: 'Governor' },
    { id: 'lt_governor',      label: 'Lieutenant Governor' },
    { id: 'state_senator',    label: 'State Senator' },
    { id: 'state_rep',        label: 'State Representative / Delegate' },
    { id: 'attorney_general', label: 'Attorney General (state or federal)' },
    { id: 'mayor',            label: 'Mayor' },
    { id: 'city_council',     label: 'City / Town Council Member' },
    { id: 'county_commissioner', label: 'County Commissioner / Supervisor' },
    { id: 'school_board',     label: 'School Board Member' },
    { id: 'sheriff',          label: 'Sheriff' },
    { id: 'judge',            label: 'Judge (elected)' },
    { id: 'other',            label: 'Other elected or appointed official' },
];

const CONCERN_TYPES = [
    { id: 'corruption',      label: 'Corruption or self-dealing',         desc: 'Bribery, kickbacks, conflicts of interest, no-bid contracts' },
    { id: 'abuse_power',     label: 'Abuse of power or authority',        desc: 'Exceeding legal authority, intimidation, retaliation' },
    { id: 'ethics',          label: 'Ethics violation',                   desc: 'Violation of ethics rules, disclosure requirements, or conduct standards' },
    { id: 'constitutional',  label: 'Constitutional violation',           desc: 'Actions that violate citizens\' constitutional rights' },
    { id: 'dereliction',     label: 'Dereliction of duty',                desc: 'Failure to perform required duties or enforce the law' },
    { id: 'misuse_funds',    label: 'Misuse of public funds',             desc: 'Improper spending, embezzlement, or misappropriation' },
    { id: 'campaign_finance',label: 'Campaign finance violation',         desc: 'Illegal donations, unreported contributions, or FEC violations' },
    { id: 'other',           label: 'Other misconduct or concern',        desc: 'Any other action warranting accountability' },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: i + 1 <= step ? '#8b1a1a' : '#ede8dc', border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600, color: i + 1 <= step ? '#f5f0e8' : '#8a8070', cursor: i + 1 < step ? 'pointer' : 'default' }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />}
            </React.Fragment>
        ))}
    </div>
);

const iStyle = { width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' };
const lStyle = { fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' };
const btnPrimary = { background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' };

// ============================================================
// Main component
// ============================================================
const OfficialAccountabilityPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        officialName:  '',
        position:      '',
        state:         '',
        concernTypes:  [],
        conduct:       '',
        evidence:      '',
        priorActions:  '',
        desiredOutcome:''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));
    const toggleConcern = (id) => setForm(p => ({ ...p, concernTypes: p.concernTypes.includes(id) ? p.concernTypes.filter(c => c !== id) : [...p.concernTypes, id] }));

    const selectedPosition = POSITION_TYPES.find(p => p.id === form.position);
    const isFederal = ['us_president','us_senator','us_representative'].includes(form.position);

    const canProceed = form.officialName && form.position && form.conduct.trim().length > 20 && form.concernTypes.length > 0;

    const generate = async () => {
        setLoading(true);
        setError('');

        const concerns = CONCERN_TYPES.filter(c => form.concernTypes.includes(c.id)).map(c => c.label).join(', ');

        const prompt = `You are an expert in government accountability, ethics law, constitutional law, and civic action. Provide a comprehensive accountability analysis and action guide.

OFFICIAL INFORMATION:
- Name: ${form.officialName}
- Position: ${selectedPosition?.label}
- ${isFederal ? 'Federal official' : `State: ${form.state}`}

CONCERN TYPE(S): ${concerns}

CONDUCT BEING CHALLENGED:
${form.conduct}

${form.evidence ? `EVIDENCE OR DOCUMENTATION AVAILABLE:\n${form.evidence}` : ''}
${form.priorActions ? `PRIOR ACTIONS TAKEN:\n${form.priorActions}` : ''}
${form.desiredOutcome ? `DESIRED OUTCOME:\n${form.desiredOutcome}` : ''}

Provide a thorough accountability guide with these sections:

## WHO THIS OFFICIAL IS ACCOUNTABLE TO
- Direct oversight bodies for this position
- Who has the power to investigate, discipline, or remove this official
- Relevant ethics commissions or oversight agencies
- Inspector General offices if applicable
- ${isFederal ? 'Congressional oversight committees' : `${form.state} state oversight mechanisms`}

## APPLICABLE ETHICS LAWS AND STANDARDS
- Specific ethics laws, statutes, or codes of conduct that apply to this position
- What constitutes a violation of these standards
- Disclosure requirements they are subject to
- Relevant federal or state statutes that may have been violated

## HOW TO FILE A FORMAL COMPLAINT
Step-by-step process for filing complaints with:
1. The most relevant oversight body
2. The relevant ethics commission
3. Law enforcement if criminal conduct is involved
4. What information to include in your complaint
5. How to document your complaint properly

## REMOVAL OR RECALL PROCESS
- Can this official be recalled by voters? How?
- Can they be removed by another body? Who and how?
- Impeachment process if applicable
- Censure or other formal sanction options
- Resignation pressure campaigns

## LEGAL OPTIONS FOR CITIZENS
- Civil rights lawsuits if applicable
- Qui tam actions if fraud on government is involved
- Public interest litigation options
- Organizations that litigate government accountability cases

## INVESTIGATIVE AND MEDIA RESOURCES
- Which journalists or media outlets cover this type of story
- Relevant watchdog organizations
- How to file a public records request for related documents
- Online databases of public records for this official

## BUILDING PUBLIC PRESSURE
- Effective citizen pressure tactics
- Organizing community response
- Public comment opportunities
- How to engage other elected officials in oversight

## REALISTIC ASSESSMENT
- Honest assessment of likelihood of accountability given the facts
- What outcomes are realistic vs aspirational
- Timeline expectations
- What success looks like in this situation

## IMMEDIATE NEXT STEPS
5 specific, prioritized actions the citizen should take right now.

Be specific, practical, and honest. Cite actual oversight bodies, ethics commissions, and statutes by name. If the conduct described does not clearly rise to the level of actionable misconduct, say so honestly and explain what threshold would need to be met.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('boc_token')}` },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');
            setResult({ analysis: text, officialName: form.officialName, position: selectedPosition?.label });
            setStep(3);
        } catch (err) {
            setError('Failed to generate analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadWord = () => {
        downloadReportAsWord(
            result.analysis,
            `Accountability-${result.officialName.replace(/[^a-z0-9]/gi,'-').slice(0,30)}`
        );
    };

    const reset = () => { setStep(1); setResult(null); setError(''); setForm({ officialName: '', position: '', state: '', concernTypes: [], conduct: '', evidence: '', priorActions: '', desiredOutcome: '' }); };

    return (
        <>
            <Helmet><title>Elected Official Accountability — By Our Consent</title></Helmet>
            <Nav />

            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>← Premium tools</Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>Official accountability suite</div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>Elected Official Accountability</h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Find out exactly who an official is accountable to, what oversight exists, how to file complaints,
                        and what steps you can take when they fail to do their job or break the law.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* STEP 1 */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Who is the official and what are your concerns?
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Describe the conduct — be factual and specific</div>
                                <div style={{ marginBottom: '6px' }}>Stick to what you know happened. Dates, specific actions, and direct quotes or official documents are most useful. The AI can only provide accurate accountability guidance if it understands what actually occurred.</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "On March 10, 2026, Mayor Smith awarded a $2.3M no-bid contract to ABC Construction, which is owned by his brother-in-law. The city charter requires competitive bidding for contracts over $50,000. No waiver was filed."
                                </div>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Evidence — list what you have</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "I have a copy of the contract from a public records request, the city charter section on bidding requirements, and emails between the mayor's office and ABC Construction obtained via FOIA."
                                </div>
                            </div>
                        </details>

                        {/* Official name */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Official's full name *</label>
                            <input style={iStyle} type="text" placeholder="e.g. John Smith"
                                value={form.officialName} onChange={e => set('officialName', e.target.value)} />
                        </div>

                        {/* Position */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Position / title *</label>
                            <select style={iStyle} value={form.position} onChange={e => set('position', e.target.value)}>
                                <option value="">Select position...</option>
                                {POSITION_TYPES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                        </div>

                        {/* State */}
                        {form.position && !isFederal && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>State *</label>
                                <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                    <option value="">Select state...</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Concern types */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Type of concern (select all that apply) *</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {CONCERN_TYPES.map(c => (
                                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.concernTypes.includes(c.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.concernTypes.includes(c.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{c.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{c.desc}</div>
                                        </div>
                                        <input type="checkbox" checked={form.concernTypes.includes(c.id)} onChange={() => toggleConcern(c.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Conduct */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Describe the conduct or concern *</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={5}
                                placeholder="Describe specifically what the official did or failed to do. Include dates, actions, and relevant details..."
                                value={form.conduct} onChange={e => set('conduct', e.target.value)} />
                        </div>

                        {/* Evidence */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Evidence or documentation available (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="What evidence do you have? Documents, emails, public records, witness statements, photos, etc..."
                                value={form.evidence} onChange={e => set('evidence', e.target.value)} />
                        </div>

                        {/* Prior actions */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Prior actions taken (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                                placeholder="Have you already contacted anyone, filed a complaint, or taken other steps?..."
                                value={form.priorActions} onChange={e => set('priorActions', e.target.value)} />
                        </div>

                        {/* Desired outcome */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Desired outcome (optional)</label>
                            <input style={iStyle} type="text"
                                placeholder="e.g. Resignation, criminal prosecution, policy reversal, public apology..."
                                value={form.desiredOutcome} onChange={e => set('desiredOutcome', e.target.value)} />
                        </div>

                        <button style={{ ...btnPrimary, opacity: !canProceed ? 0.5 : 1 }} disabled={!canProceed} onClick={() => setStep(2)}>
                            Review & generate →
                        </button>
                    </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Review your request
                        </div>

                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px', fontSize: '13px', fontFamily: 'sans-serif' }}>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Official:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.officialName}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Position:</div>
                                <div style={{ color: '#1e2a3a' }}>{selectedPosition?.label}{!isFederal && form.state ? ` · ${form.state}` : ''}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Concerns:</div>
                                <div style={{ color: '#1e2a3a' }}>{CONCERN_TYPES.filter(c => form.concernTypes.includes(c.id)).map(c => c.label).join(', ')}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Conduct:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.conduct}</div>
                            </div>
                        </div>

                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                            Review the information above. Click ① to go back and make changes. When ready, generate your accountability guide.
                        </div>

                        {error && <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>{error}</div>}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading} onClick={generate}>
                                {loading ? 'Generating guide...' : 'Generate Accountability Guide →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Researching oversight bodies, ethics laws, and accountability options...
                                <br />This may take 20-30 seconds.
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 3 */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>Accountability guide ready</div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>{result.officialName} · {result.position}</div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Guide</button>
                        </div>

                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) return <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>{line.replace('## ', '')}</div>;
                                if (line.startsWith('- ') || line.startsWith('• ')) return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Guide</button>
                            <button onClick={reset} style={btnSecondary}>New accountability request</button>
                        </div>

                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This guide is for educational and civic engagement purposes. It is not legal advice.
                            For complex situations involving potential criminal conduct, consult a qualified attorney.
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default OfficialAccountabilityPage;
